import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { ImagePreview } from '../ImagePreview';
import { processImageFiles, cleanupImagePreviews, getImageErrorMessage } from '../../utils/imageUtils';
import type { ImageFile } from '../../types/chat.types';
import { useTheme, usePreset } from '../../lib/ThemeContext';

interface MessageInputProps {
  onSend: (message: string, images: string[]) => void;
  disabled?: boolean;
}

const MAX_IMAGES = 5;

export const MessageInput: React.FC<MessageInputProps> = ({ onSend, disabled = false }) => {
  const { theme, t } = useTheme();
  const preset = usePreset();
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const headerStyle = theme?.headerStyle || 'glass';
  const glassOpacity = theme?.headerGlassOpacity ?? 0.12;
  const isFlat = headerStyle === 'flat';
  const isNv = preset === 'nv';
  const isNevi = preset === 'nevi';
  const [shouldFocus, setShouldFocus] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled && shouldFocus && textareaRef.current) {
      textareaRef.current.focus();
      setShouldFocus(false);
    }
  }, [disabled, shouldFocus]);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, []);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const availableSlots = MAX_IMAGES - images.length;
    if (availableSlots <= 0) {
      alert(`${t.messageInput.maxImages} ${MAX_IMAGES}`);
      return;
    }
    const fileArray = Array.from(files).slice(0, availableSlots);
    if (files.length > availableSlots) {
      alert(`${t.messageInput.moreImages} ${availableSlots} (max ${MAX_IMAGES})`);
    }
    const errors = fileArray.map(getImageErrorMessage).filter(Boolean);
    if (errors.length > 0) {
      alert(errors.join('\n'));
    }
    const newImages = await processImageFiles(fileArray);
    setImages((prev) => [...prev, ...newImages]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (id: string) => {
    setImages((prev) => {
      const imageToRemove = prev.find((img) => img.id === id);
      if (imageToRemove) cleanupImagePreviews([imageToRemove]);
      return prev.filter((img) => img.id !== id);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleSend = () => {
    if (disabled) return;
    const trimmedMessage = message.trim();
    if (!trimmedMessage && images.length === 0) return;
    const imageDataUrls = images.map((img) => img.dataUrl);
    setShouldFocus(true);
    onSend(trimmedMessage, imageDataUrls);
    cleanupImagePreviews(images);
    setMessage('');
    setImages([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      const dataTransfer = new DataTransfer();
      imageFiles.forEach((file) => dataTransfer.items.add(file));
      handleFileSelect(dataTransfer.files);
    }
  };

  const canSend = message.trim() || images.length > 0;

  return (
    <div className="wc-relative wc-overflow-hidden">
      {/* Background layer */}
      {isNv || isNevi ? (
        <div className="wc-absolute wc-inset-0" style={{ backgroundColor: 'transparent' }} />
      ) : isFlat ? (
        <div
          className="wc-absolute wc-inset-0"
          style={{
            backgroundColor: 'var(--wc-background-color)',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        />
      ) : (
        <>
          <div
            className="wc-absolute wc-inset-0"
            style={{
              background: `linear-gradient(0deg, rgba(255, 255, 255, ${glassOpacity}) 0%, rgba(255, 255, 255, ${glassOpacity * 0.4}) 50%, rgba(255, 255, 255, 0) 100%)`,
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            }}
          />
          <div
            className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-h-[1px]"
            style={{
              background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, ${glassOpacity * 1.5}) 30%, rgba(255, 255, 255, ${glassOpacity * 2}) 50%, rgba(255, 255, 255, ${glassOpacity * 1.5}) 70%, transparent 100%)`,
            }}
          />
          <div
            className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-h-[1px]"
            style={{
              background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, ${glassOpacity * 2.5}) 20%, rgba(255, 255, 255, ${glassOpacity * 4}) 50%, rgba(255, 255, 255, ${glassOpacity * 2.5}) 80%, transparent 100%)`,
            }}
          />
        </>
      )}

      {/* Content */}
      <div className="wc-relative">
        <ImagePreview images={images} onRemove={handleRemoveImage} />

        <div
          className={`${isNv || isNevi ? '' : 'wc-px-3 wc-py-2'} ${isDragging ? 'wc-bg-[var(--wc-primary-color)]/10' : ''}`}
          style={isNv ? { padding: '0 16px 16px' } : isNevi ? { padding: '0 14px 14px' } : undefined}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            onChange={handleFileInputChange}
            className="wc-hidden"
          />

          {isNevi ? (
            /* NeVi: Figma node 1903:19260 — dark glass row: attach button + input field with send inside */
            <div className="wc-flex wc-items-center" style={{ gap: '4px' }}>
              {/* Attach button — glass square 40px, radius 8 */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center disabled:wc-opacity-40 disabled:wc-cursor-not-allowed"
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(251, 251, 251, 0.05)',
                  border: '1px solid rgba(251, 251, 251, 0.15)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 251, 251, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 251, 251, 0.05)';
                }}
                type="button"
                aria-label={t.messageInput.attachImage}
              >
                {/* Image - Iconly Pro */}
                <svg viewBox="0 0 19.96 19.95" fill="none" style={{ width: '20px', height: '20px' }}>
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.3 0C17.685 0 19.96 2.371 19.96 5.899V14.051C19.96 17.579 17.685 19.95 14.3 19.95H5.65C2.27 19.95 0 17.579 0 14.051V5.899C0 2.371 2.27 0 5.65 0H14.3ZM14.3 1.5H5.65C3.129 1.5 1.5 3.227 1.5 5.899V14.051C1.5 16.724 3.129 18.45 5.65 18.45H14.3C16.827 18.45 18.46 16.724 18.46 14.051V5.899C18.46 3.227 16.827 1.5 14.3 1.5ZM14.939 10.0994C14.9453 10.1045 14.9516 10.1097 14.9646 10.1218L14.9839 10.1403C14.9878 10.1441 14.9921 10.1483 14.9968 10.1529L15.0527 10.2083C15.233 10.3889 15.7315 10.898 17.217 12.4234C17.506 12.7194 17.501 13.1944 17.204 13.4834C16.908 13.7744 16.432 13.7654 16.143 13.4694C16.143 13.4694 14.094 11.3664 13.948 11.2244C13.793 11.0974 13.544 11.0234 13.299 11.0474C13.05 11.0724 12.826 11.1914 12.667 11.3844C10.343 14.2034 10.315 14.2304 10.277 14.2674C9.419 15.1094 8.034 15.0954 7.191 14.2354C7.191 14.2354 6.261 13.2914 6.245 13.2724C6.014 13.0584 5.602 13.0724 5.355 13.3334L3.825 14.9464C3.677 15.1024 3.479 15.1804 3.281 15.1804C3.095 15.1804 2.91 15.1124 2.765 14.9744C2.464 14.6904 2.452 14.2144 2.737 13.9154L4.265 12.3024C5.074 11.4434 6.439 11.4014 7.302 12.2114L8.26 13.1834C8.527 13.4534 8.961 13.4584 9.229 13.1944C9.33 13.0754 11.508 10.4304 11.508 10.4304C11.922 9.9284 12.506 9.6184 13.155 9.5544C13.805 9.4974 14.436 9.6864 14.939 10.0994ZM6.5582 4.6289C7.9402 4.6299 9.0632 5.7539 9.0632 7.1329C9.0632 8.5139 7.9392 9.6379 6.5582 9.6379C5.1772 9.6379 4.0542 8.5139 4.0542 7.1329C4.0542 5.7519 5.1772 4.6289 6.5582 4.6289ZM6.5572 6.1289C6.0042 6.1289 5.5542 6.5789 5.5542 7.1329C5.5542 7.6869 6.0042 8.1379 6.5582 8.1379C7.1122 8.1379 7.5632 7.6869 7.5632 7.1329C7.5632 6.5799 7.1122 6.1299 6.5572 6.1289Z"
                    fill="#FBFBFB"
                  />
                </svg>
              </button>

              {/* Input field — glass, send button inside on the right */}
              <div
                className="wc-flex-1 wc-flex wc-items-center wc-min-w-0"
                style={{
                  gap: '8px',
                  padding: '6px 6px 6px 8px',
                  background: 'rgba(251, 251, 251, 0.05)',
                  border: '1px solid rgba(251, 251, 251, 0.15)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder={t.messageInput.placeholder}
                  disabled={disabled}
                  className="wc-flex-1 wc-min-w-0 wc-resize-none wc-border-none wc-bg-transparent wc-overflow-hidden focus:wc-outline-none disabled:wc-opacity-50 disabled:wc-cursor-not-allowed wc-nevi-input"
                  style={{
                    fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#FBFBFB',
                    padding: '4px 0 4px 4px',
                  }}
                  rows={1}
                  maxLength={4000}
                />

                {/* Send button — Figma node I1903:28121;135:3697 */}
                <button
                  onClick={handleSend}
                  disabled={disabled || !canSend}
                  className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center wc-self-end disabled:wc-cursor-not-allowed focus:wc-outline-none"
                  style={{
                    width: '28px',
                    height: '28px',
                    background: disabled || !canSend ? 'rgba(251, 251, 251, 0.5)' : '#F9AA66',
                    border: '1px solid rgba(251, 251, 251, 0.15)',
                    borderRadius: '8px',
                    opacity: disabled || !canSend ? 0.5 : 1,
                    color: '#2B323B',
                    transition: 'opacity 0.15s ease, background 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (canSend && !disabled) e.currentTarget.style.background = '#fbbc84';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = disabled || !canSend ? 'rgba(251, 251, 251, 0.5)' : '#F9AA66';
                  }}
                  type="button"
                  aria-label={t.messageInput.sendMessage}
                >
                  {/* Arrow Right - Iconly Pro */}
                  <svg viewBox="0 0 13.75 11.2909" fill="none" style={{ width: '16px', height: '13px' }}>
                    <path
                      d="M7.53255 0.328794C7.69633 0.0249437 8.07587 -0.0888903 8.37972 0.0748873H8.38053L8.38135 0.0757011C8.38215 0.0761366 8.38328 0.0766096 8.3846 0.0773287C8.38751 0.0789049 8.39161 0.0818101 8.39681 0.0846529C8.40735 0.0904115 8.42297 0.0983105 8.44238 0.109067C8.48156 0.130783 8.53856 0.162619 8.61003 0.203468C8.75289 0.285131 8.95564 0.404078 9.1984 0.553403C9.68276 0.85133 10.3323 1.2752 10.9847 1.77573C11.6333 2.27343 12.305 2.86267 12.8206 3.49367C13.3242 4.10994 13.75 4.85216 13.75 5.64455L13.7451 5.79185C13.6967 6.52894 13.2936 7.21805 12.8215 7.79624C12.3059 8.42755 11.6333 9.01627 10.9847 9.51418C10.3323 10.015 9.68277 10.4393 9.1984 10.7373C8.95562 10.8867 8.75289 11.0056 8.61003 11.0873C8.53857 11.1281 8.48156 11.1599 8.44238 11.1817C8.42322 11.1923 8.40814 11.2003 8.39762 11.2061C8.39232 11.209 8.38755 11.2118 8.3846 11.2134C8.38329 11.2141 8.38215 11.2146 8.38135 11.215L8.38053 11.2158H8.37972C8.07592 11.3796 7.69639 11.2665 7.53255 10.9627C7.36874 10.6589 7.48263 10.2794 7.78646 10.1156C7.78681 10.1154 7.78795 10.1153 7.7889 10.1148C7.79079 10.1137 7.79379 10.1121 7.79785 10.1099C7.80632 10.1053 7.81958 10.0975 7.83691 10.0879C7.87155 10.0687 7.92295 10.0395 7.98909 10.0016C8.12194 9.92569 8.31344 9.8143 8.54329 9.67287C9.00417 9.38928 9.61508 8.98958 10.223 8.52297C10.8347 8.05343 11.4234 7.5313 11.853 7.00523C12.0693 6.74039 12.2283 6.49434 12.3348 6.27036H0.625C0.279822 6.27036 0 5.99054 0 5.64536C2.083e-05 5.3002 0.279835 5.02036 0.625 5.02036H12.3356C12.2291 4.79619 12.0698 4.54998 11.853 4.28469C11.4234 3.75892 10.8355 3.23628 10.2238 2.76694C9.6158 2.30045 9.00424 1.90138 8.54329 1.61786C8.31367 1.47662 8.12273 1.365 7.98991 1.28908C7.92362 1.25119 7.87158 1.22203 7.83691 1.20282C7.81965 1.19325 7.80632 1.18629 7.79785 1.18166C7.79364 1.17936 7.79081 1.177 7.7889 1.17596C7.78795 1.17545 7.78681 1.17534 7.78646 1.17515C7.4828 1.01142 7.3691 0.63255 7.53255 0.328794Z"
                      fill="#2B323B"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ) : isNv ? (
            /* NV: Figma — white container, padding 8px, gap 20px, border-radius 16px, height 56px */
            <div
              className="wc-flex wc-items-center"
              style={{
                padding: '8px',
                gap: '20px',
                background: '#FFFFFF',
                borderRadius: '16px',
                minHeight: '56px',
              }}
            >
              {/* Attach button — 40px, border 1px solid rgba(46,46,46,0.15), border-radius 100px */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center disabled:wc-opacity-40 disabled:wc-cursor-not-allowed"
                style={{
                  width: '40px',
                  height: '40px',
                  background: '#FFFFFF',
                  border: '1px solid rgba(46, 46, 46, 0.15)',
                  borderRadius: '100px',
                  transition: 'opacity 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                type="button"
                aria-label={t.messageInput.attachImage}
              >
                {/* Iconly Image-2 icon — 24px, color #2E2E2E */}
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ width: '24px', height: '24px', color: '#2E2E2E' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>

              {/* Text input — Figma: font-size 12px, color rgba(46,46,46,0.5) placeholder */}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                onPaste={handlePaste}
                placeholder={t.messageInput.placeholder}
                disabled={disabled}
                className="wc-flex-1 wc-min-w-0 wc-resize-none wc-border-none wc-bg-transparent wc-overflow-hidden focus:wc-outline-none disabled:wc-opacity-50 disabled:wc-cursor-not-allowed"
                style={{
                  fontFamily: "'WC-Involve', sans-serif",
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#2E2E2E',
                }}
                rows={1}
                maxLength={4000}
              />

              {/* Send button — 40px, disabled: bg rgba(46,46,46,0.5) opacity 0.3 */}
              <button
                onClick={handleSend}
                disabled={disabled || !canSend}
                className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center wc-border-none disabled:wc-cursor-not-allowed focus:wc-outline-none"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '100px',
                  background:
                    disabled || !canSend
                      ? 'rgba(46, 46, 46, 0.5)'
                      : 'linear-gradient(104.04deg, #FE7338 0%, #EA3700 100%)',
                  opacity: disabled || !canSend ? 0.3 : 1,
                  color: '#FFFFFF',
                  transition: 'opacity 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (canSend) e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = disabled || !canSend ? '0.3' : '1';
                }}
                type="button"
                aria-label={t.messageInput.sendMessage}
              >
                {/* arrow_line icon — 24px */}
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '24px', height: '24px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-6 6m6-6l6 6" />
                </svg>
              </button>
            </div>
          ) : isFlat ? (
            /* Flat: unified bordered container with inline buttons */
            <div
              className="wc-flex wc-items-center wc-rounded-lg"
              style={{
                border: '1px solid rgba(0, 0, 0, 0.15)',
                background: 'var(--wc-background-color)',
              }}
            >
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center wc-w-10 wc-h-10 wc-border-none wc-rounded-l-lg wc-transition-colors wc-duration-150 hover:wc-bg-black/[0.03] disabled:wc-opacity-40 disabled:wc-cursor-not-allowed"
                style={{ background: 'transparent', color: 'var(--wc-text-color)' }}
                type="button"
                aria-label={t.messageInput.attachImage}
              >
                <svg
                  className="wc-w-[18px] wc-h-[18px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ opacity: 0.5 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>

              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                onPaste={handlePaste}
                placeholder={t.messageInput.placeholder}
                disabled={disabled}
                className="wc-flex-1 wc-min-w-0 wc-resize-none wc-border-none wc-bg-transparent wc-text-[var(--wc-input-text-color)] wc-py-2.5 wc-text-sm wc-overflow-hidden focus:wc-outline-none disabled:wc-opacity-50 disabled:wc-cursor-not-allowed"
                style={{ fontFamily: 'inherit' }}
                rows={1}
                maxLength={4000}
              />

              <button
                onClick={handleSend}
                disabled={disabled || !canSend}
                className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center wc-w-10 wc-h-10 wc-border-none wc-rounded-r-lg wc-transition-colors wc-duration-150 disabled:wc-cursor-not-allowed focus:wc-outline-none"
                style={{
                  background: disabled || !canSend ? 'transparent' : 'var(--wc-primary-color)',
                  color: disabled || !canSend ? 'var(--wc-text-color)' : '#ffffff',
                  opacity: disabled || !canSend ? 0.3 : 1,
                }}
                type="button"
                aria-label={t.messageInput.sendMessage}
              >
                <svg className="wc-w-[18px] wc-h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                  />
                </svg>
              </button>
            </div>
          ) : (
            /* Glass: separate elements */
            <div className="wc-flex wc-items-center wc-gap-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="wc-flex-shrink-0 wc-p-1 wc-w-8 wc-h-8 wc-flex wc-items-center wc-justify-center wc-rounded-full wc-border-none wc-transition-all wc-duration-200 hover:wc-scale-110 disabled:wc-opacity-50 disabled:wc-cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%)',
                  boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.15), 0 1px 2px rgba(0, 0, 0, 0.15)',
                }}
                type="button"
                aria-label={t.messageInput.attachImage}
              >
                <svg
                  className="wc-w-5 wc-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>

              <div
                className="wc-flex-1 wc-relative wc-rounded-2xl wc-overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  boxShadow:
                    'inset 0 1px 1px rgba(255, 255, 255, 0.1), inset 0 -1px 1px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder={t.messageInput.placeholder}
                  disabled={disabled}
                  className="wc-w-full wc-resize-none wc-border-none wc-bg-transparent wc-text-[var(--wc-input-text-color)] wc-px-3 wc-py-1.5 wc-text-sm wc-overflow-hidden focus:wc-outline-none disabled:wc-opacity-50 disabled:wc-cursor-not-allowed"
                  style={{ fontFamily: 'inherit', textShadow: '0 1px 1px rgba(0, 0, 0, 0.2)' }}
                  rows={1}
                  maxLength={4000}
                />
              </div>

              <button
                onClick={handleSend}
                disabled={disabled || !canSend}
                className="wc-flex-shrink-0 wc-p-2 wc-w-8 wc-h-8 wc-flex wc-items-center wc-justify-center wc-rounded-full wc-border-none wc-transition-all wc-duration-200 hover:wc-scale-110 disabled:wc-opacity-30 disabled:wc-cursor-not-allowed focus:wc-outline-none"
                style={{
                  background:
                    disabled || !canSend
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  boxShadow:
                    disabled || !canSend
                      ? 'inset 0 1px 1px rgba(255, 255, 255, 0.1)'
                      : 'inset 0 1px 1px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(139, 92, 246, 0.3)',
                }}
                type="button"
                aria-label={t.messageInput.sendMessage}
              >
                <svg
                  className="wc-w-4 wc-h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: disabled || !canSend ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.9)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                  />
                </svg>
              </button>
            </div>
          )}

          {isDragging && (
            <div className="wc-mt-2 wc-text-center wc-text-[var(--wc-primary-color)] wc-text-sm">
              {t.messageInput.dropImages}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
